import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Container from '../../components/ui/Container';
import PageHero from '../../components/ui/PageHero';
import BlogCard from '../../components/blog/BlogCard';
import Pagination from '../../components/blog/Pagination';
import { motion } from 'framer-motion';
import { apiFetch } from '../../utils/api';
import {
  FaSearch,
  FaTimes,
  FaStar,
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaFilter,
} from 'react-icons/fa';

const POSTS_PER_PAGE = 9;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const initialCategory = searchParams.get('category') || 'All';
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [currentPage, setCurrentPage] = useState(1);

  // Sync category with URL search param if changed externally
  useEffect(() => {
    const catFromUrl = searchParams.get('category');
    if (catFromUrl) {
      setSelectedCategory(catFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await apiFetch('/api/blogs');
        if (!res.ok) {
          throw new Error('Failed to fetch published blogs');
        }
        const data = await res.json();
        setBlogs(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Unable to load blogs');
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Compute category list ONLY from published blogs that actually have data
  const categoriesWithCounts = useMemo(() => {
    const countMap = {};
    blogs.forEach((b) => {
      const c = b.category?.trim();
      if (c) {
        countMap[c] = (countMap[c] || 0) + 1;
      }
    });

    const activeList = Object.keys(countMap)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({
        name,
        count: countMap[name],
      }));

    return [{ name: 'All', count: blogs.length }, ...activeList];
  }, [blogs]);

  // Filtered blogs
  const filteredBlogs = useMemo(() => {
    return blogs.filter((blog) => {
      const matchCat =
        selectedCategory === 'All' ||
        (blog.category && blog.category.toLowerCase() === selectedCategory.toLowerCase());

      const query = searchQuery.trim().toLowerCase();
      const matchQuery =
        !query ||
        blog.title?.toLowerCase().includes(query) ||
        blog.shortDescription?.toLowerCase().includes(query) ||
        blog.content?.toLowerCase().includes(query) ||
        blog.authorName?.toLowerCase().includes(query) ||
        blog.author?.name?.toLowerCase().includes(query) ||
        (Array.isArray(blog.tags) && blog.tags.some((t) => t.toLowerCase().includes(query)));

      return matchCat && matchQuery;
    });
  }, [blogs, selectedCategory, searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  // Determine if we show a Featured Hero Article
  const isDefaultView = selectedCategory === 'All' && !searchQuery.trim();
  const featuredBlog = isDefaultView && filteredBlogs.length > 0 ? filteredBlogs[0] : null;
  const regularBlogs = isDefaultView && filteredBlogs.length > 0 ? filteredBlogs.slice(1) : filteredBlogs;

  // Pagination for regular blogs
  const totalPages = Math.ceil(regularBlogs.length / POSTS_PER_PAGE);
  const paginatedBlogs = regularBlogs.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
  };

  return (
    <div className="w-full overflow-x-hidden pt-16 lg:pt-20">
      {/* Hero Section */}
      <PageHero
        badge="KNOWLEDGE & PERSPECTIVES"
        title="Insights, Strategies & Innovation"
        subtitle="Expert analysis on corporate finance, legal compliances, and enterprise tech innovation."
      />

      {/* Main Content Section */}
      <section className="section-pad bg-[#f8fafc]/80 min-h-screen">
        <Container>
          {/* Controls Bar: Search & Category Filter Pills */}
          <motion.div
            variants={fadeUp}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.15 }}
            className="mb-10 space-y-5"
          >
            {/* Top row: Search Input & Result count */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              {/* Search box */}
              <div className="relative flex-1 max-w-xl">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles by title, keywords, or topics..."
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-10 text-sm text-slate-800 shadow-2xs placeholder:text-slate-400 focus:border-[#01295c] focus:outline-none focus:ring-3 focus:ring-blue-100 transition"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition p-1"
                    title="Clear search"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                )}
              </div>

              {/* Status info */}
              <div className="flex items-center justify-between sm:justify-end gap-3 text-xs text-slate-500 font-medium">
                <span>
                  Showing <strong className="text-slate-800">{filteredBlogs.length}</strong> {filteredBlogs.length === 1 ? 'article' : 'articles'}
                </span>
                {(searchQuery || selectedCategory !== 'All') && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-amber-600 hover:text-amber-700 underline font-semibold transition cursor-pointer"
                  >
                    Reset filters
                  </button>
                )}
              </div>
            </div>

            {/* Category filter pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1 shrink-0">
                <FaFilter className="text-[10px]" /> Topics:
              </span>
              {categoriesWithCounts.map(({ name, count }) => {
                const isActive = selectedCategory.toLowerCase() === name.toLowerCase();
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(name);
                      if (name === 'All') {
                        searchParams.delete('category');
                      } else {
                        searchParams.set('category', name);
                      }
                      setSearchParams(searchParams, { replace: true });
                    }}
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 cursor-pointer ${
                      isActive
                        ? 'bg-[#01295c] text-white shadow-md shadow-blue-900/15 ring-2 ring-[#01295c]/20'
                        : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 shadow-2xs'
                    }`}
                  >
                    <span>{name}</span>
                    <span
                      className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                        isActive
                          ? 'bg-amber-400 text-slate-950'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Loading Skeleton */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs animate-pulse space-y-4"
                >
                  <div className="h-48 rounded-xl bg-slate-200" />
                  <div className="h-4 w-1/3 rounded bg-slate-200" />
                  <div className="h-6 w-3/4 rounded bg-slate-200" />
                  <div className="h-12 w-full rounded bg-slate-100" />
                  <div className="h-8 w-full rounded bg-slate-100" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-xs">
              <p className="font-semibold text-base mb-1">Failed to load articles</p>
              <p className="text-sm opacity-80">{error}</p>
            </div>
          ) : filteredBlogs.length === 0 ? (
            /* Empty State */
            <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-xs">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-500">
                <FaSearch className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">No articles found</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                We couldn't find any articles matching your search query or topic filter. Try broadening your keywords.
              </p>
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn-primary text-sm py-2.5 px-6 rounded-xl"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="space-y-12">
              {/* Featured Hero Card (Shown on default view) */}
              {featuredBlog && (
                <motion.div
                  variants={fadeUp}
                  initial="initial"
                  animate="animate"
                  transition={{ delay: 0.2 }}
                  className="group relative overflow-hidden rounded-3xl border border-slate-200/90 bg-white shadow-md hover:shadow-2xl transition-all duration-300"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12">
                    {/* Featured Image */}
                    <Link
                      to={`/blogs/${featuredBlog.slug}`}
                      className="relative lg:col-span-7 h-72 sm:h-96 lg:h-auto overflow-hidden bg-slate-900 block"
                    >
                      {featuredBlog.featuredImage ? (
                        <img
                          src={featuredBlog.featuredImage}
                          alt={featuredBlog.title}
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-slate-400">
                          <span className="font-bold uppercase tracking-wider">Featured Story</span>
                        </div>
                      )}
                      {/* Featured Badge */}
                      <div className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-md">
                        <FaStar className="text-amber-200 text-xs" />
                        <span>FEATURED STORY</span>
                      </div>
                    </Link>

                    {/* Featured Content Details */}
                    <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
                      <div>
                        {/* Meta: Category & Date */}
                        <div className="mb-3 flex items-center gap-2.5 text-xs text-slate-500">
                          {featuredBlog.category && (
                            <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-[#01295c]">
                              {featuredBlog.category}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <FaCalendarAlt className="text-slate-400 text-[11px]" />
                            {new Date(featuredBlog.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <FaClock className="text-slate-400 text-[11px]" />
                            {featuredBlog.readingTime || 4} min read
                          </span>
                        </div>

                        {/* Title */}
                        <h2 className="mb-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 transition-colors group-hover:text-amber-600">
                          <Link to={`/blogs/${featuredBlog.slug}`}>
                            {featuredBlog.title}
                          </Link>
                        </h2>

                        {/* Excerpt */}
                        <p className="text-sm sm:text-base leading-relaxed text-slate-600 line-clamp-3 mb-6">
                          {featuredBlog.shortDescription ||
                            featuredBlog.content?.replace(/<[^>]*>/g, ' ').slice(0, 180) + '...'}
                        </p>
                      </div>

                      {/* Author & Button */}
                      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-5">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#01295c] text-xs font-bold text-white shadow-xs">
                            {(featuredBlog.author?.name || featuredBlog.authorName || 'F').charAt(0).toUpperCase()}
                          </div>
                          <div className="text-xs">
                            <p className="font-bold text-slate-800">
                              {featuredBlog.author?.name || featuredBlog.authorName || 'Finegal Team'}
                            </p>
                            <p className="text-[11px] text-slate-400">Chief Advisor</p>
                          </div>
                        </div>

                        <Link
                          to={`/blogs/${featuredBlog.slug}`}
                          className="btn-primary text-xs font-bold py-2.5 px-5 rounded-xl inline-flex items-center gap-2 shadow-sm"
                        >
                          Read Story
                          <FaArrowRight className="text-xs" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Regular Articles Section */}
              {regularBlogs.length > 0 && (
                <div>
                  {featuredBlog && (
                    <div className="mb-6 flex items-center justify-between">
                      <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
                        Latest Articles
                      </h3>
                      <span className="text-xs font-medium text-slate-500">
                        Page {currentPage} of {totalPages || 1}
                      </span>
                    </div>
                  )}

                  {/* 3-Column Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {paginatedBlogs.map((blog, i) => (
                      <motion.div
                        key={blog._id}
                        variants={fadeUp}
                        initial="initial"
                        animate="animate"
                        transition={{ delay: 0.1 + (i % 6) * 0.05 }}
                      >
                        <BlogCard blog={blog} />
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="mt-14">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => {
                          setCurrentPage(page);
                          window.scrollTo({ top: 350, behavior: 'smooth' });
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}
